import { useState, useEffect } from 'react';
import Table from '../../components/Table';
import { Grid } from '@mui/material';
import Text from 'components/Text';
import { authPostRequest } from 'Api/api';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { style } from 'constant/constants';
import AddIcon from '@mui/icons-material/Add';
import Loader from 'components/Loader';
import {
  tableOptionFilter,
  columOptions,
  isUser,
  isAreaAdmin,
  updateHistoryState
} from '../../utils/utils';
import DialogBox from 'components/DialogBox';
import DeleteSpan from 'components/DeleteSpan';
import CRUDActions from 'components/CRUDActions';
import SuccessError from 'components/SuccessError';
import CustomButton from 'components/Button';
import CreateProject from './CreateProject';
import Disclaimer from 'components/Disclaimer';
import ShowDataWithSpaces from 'components/ShowDataWithSpaces';
import { addProject, removeProject } from 'reduxStore/authSlice';
import { dateFormatter } from 'utils/dateFormatter';

const isSort = {
  sort: false,
  ...columOptions
};
const columns_optionsIDs = { filter: false, display: 'excluded' };

const columns = [
  {
    name: 'id',
    options: columns_optionsIDs
  },
  {
    name: 'name',
    label: 'Name',
    options: {
      ...isSort,
      customBodyRender: (value) => <ShowDataWithSpaces value={value} />
    }
  },
  {
    name: 'description',
    label: 'Description',
    options: {
      ...isSort,
      customBodyRender: (value) => <ShowDataWithSpaces value={value} />
    }
  },
  {
    name: 'price',
    label: 'Price ($)',
    options: {
      ...isSort,
      customBodyRender: (value) =>
        value !== null && value !== undefined && parseFloat(value).toFixed(2)
    }
  },
  {
    name: 'addDate',
    label: 'Add Date',
    options: {
      ...isSort,
      customBodyRender: (value) => dateFormatter(value)
    }
  }
];

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState({});
  const [successMessage, setSuccessMessage] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);
  const { userData, storeData, adminAccess, showDisclaimer } = useSelector((store) => store.auth);
  const [openDialog, setOpenDialog] = useState(false);
  const [isFormSaved, setIsFormSaved] = useState(false);
  const [addOpenModal, setAddOpenModal] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getAllProjects = async () => {
    const storeID = {
      storeId: adminAccess ? storeData?.id : userData?.storesList[0]?.storeId
    };
    if (
      !storeID?.storeId ||
      storeID?.storeId == undefined ||
      storeID?.storeId == null ||
      isAreaAdmin(userData?.roles)
    ) {
      // navigate('/StoreInfo/projects');
      // navigate('/Error');
      navigate('/Stores');
    }
    try {
      setShowLoader(true);
      const projects = await authPostRequest('getProjectsList', storeID);
      if (isUser(userData.roles)) {
        setProjects(projects.projectsList.filter((list) => list.addWho === userData.userId));
      } else {
        setProjects(projects.projectsList);
      }
      setShowLoader(false);
    } catch (err) {
      setShowLoader(false);
      console.log(err);
    }
  };

  // double flag used here
  // 1st flag comming from store login
  // 2nd flag comming from user login
  const historyFlag = localStorage.getItem('BeforeLoginStoreHistory');
  const historyTrack = localStorage.getItem('historyTrack');

  useEffect(() => {
    if (historyFlag || historyTrack) {
      // console.log('612:this is my image');
      // history.replaceState(null, '', window.location.href);
      // const newURL = window.location.pathname + window.location.search + window.location.hash;
      // history.replaceState({}, '', newURL);
      // Replace the current state multiple times to clear the history
      // for (let i = 0; i < history.length; i++) {
      //   // history.replaceState(null, '', window.location.href);
      //   history.replaceState({}, '', newURL);
      // }
      localStorage.removeItem('BeforeLoginStoreHistory');
      localStorage.removeItem('historyTrack');
    }
    if (history.state == null) {
      history.pushState(null, '', '/StoreInfo/projects');
      navigate('/StoreInfo/projects');
      updateHistoryState();
    }
  }, [historyFlag, historyTrack]);

  useEffect(() => {
    getAllProjects();
  }, [isFormSaved, showDisclaimer]);

  //Handle confirmation for delete
  const handleConfirm = async () => {
    setErrorMessage([]);
    setSuccessMessage([]);
    setShowAlertBar(false);

    const params = {
      userId: userData.userId,
      projectId: project.id
    };

    try {
      setShowLoader(true);
      setShowAlertBar(false);
      setIsFormSaved(false);
      const deleteProject = await authPostRequest('deleteProject', params);
      if (deleteProject?.resStatus === 1) {
        setOpenDialog(!openDialog);
        setSuccessMessage([deleteProject.resMessage]);
        dispatch(removeProject(project?.id));
      } else {
        setErrorMessage([deleteProject.resMessage]);
      }
      setShowLoader(false);
      setIsFormSaved(true);

      setShowAlertBar(true);
      setOpenDialog(false);
      window.scrollTo(0, 0);
    } catch (err) {
      setShowLoader(false);
      setErrorMessage(['Error']);
      window.scrollTo(0, 0);
    }
  };

  //state set from delete project
  const handleDialogState = () => {
    setOpenDialog(!openDialog);
  };

  //for delete functionalities
  const handleDialogOpen = (project) => {
    setProject(projects[project]);
    setOpenDialog(true);
  };
  return (
    <>
      <Grid display="flex" alignItems="center" justifyContent="space-between">
        <Text variant="h4" style={{ color: style.LIGHT.primary }}>
          Projects
        </Text>
        <CustomButton
          add={true}
          type="submit"
          endIcon={<AddIcon />}
          onClick={() => setAddOpenModal(true)}>
          Add
        </CustomButton>
      </Grid>
      {showAlertBar && (
        <SuccessError
          successMessage={successMessage}
          errorMessage={errorMessage}
          setShowAlertBar={setShowAlertBar}
        />
      )}
      <Table
        data={projects}
        options={tableOptionFilter}
        columns={[
          ...columns,
          {
            name: 'actions',
            label: 'Action',
            options: {
              filter: false,
              sort: false,
              customBodyRender: (value, tableMeta) => {
                return (
                  <Grid sx={{ ml: '-1.5vh' }}>
                    <CRUDActions
                      onDelete={() => handleDialogOpen(tableMeta.rowIndex)}
                      onEdit={() => {
                        dispatch(addProject(projects[tableMeta.rowIndex]));

                        navigate('/StoreInfo/roof-section', {
                          state: { index: projects[tableMeta.rowIndex] }
                        });
                      }}
                    />
                  </Grid>
                );
              }
            }
          }
        ]}
      />

      {!showDisclaimer && showLoader && <Loader open={showLoader} />}
      {openDialog && (
        <DialogBox
          handleDialogBoxState={handleDialogState}
          openDialogBox={openDialog}
          handleConfirmAction={handleConfirm}
          deleteMessage={<DeleteSpan message="project" toBeDel={project.name} />}
        />
      )}
      {addOpenModal && (
        <CreateProject
          openModal={addOpenModal}
          setOpenModal={setAddOpenModal}
          setIsFormSaved={setIsFormSaved}
          isFormSaved={isFormSaved}
        />
      )}

      {showDisclaimer && (
        <Disclaimer
          setErrorMessage={setErrorMessage}
          setSuccessMessage={setSuccessMessage}
          setShowAlertBar={setShowAlertBar}
        />
      )}
    </>
  );
};

export default Projects;
